import { CartContext } from '@context/cart.context';
import Button from '@components/atoms/button/button';
import { useNegotiation } from '@context/negotiation.context';
import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { useContextSelector } from 'use-context-selector';
import clsx from 'clsx';
import useProductQuery from '@hooks/product/use-product-query';
import usePdpLayout from '@hooks/product/use-pdp-layout';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useFeatureFlag from '@hooks/use-feature-flags';
import { ToastMessageContext } from '@context/toast-message.context';
import Link from '@components/atoms/link/link';
import Icon from '@components/atoms/icon/icon';
import styles from '../CTA-product.module.scss';

export type AddToCartButtonProps = {
  triggerAddToCart?: boolean;
  className?: string;
};

const AddToCartButton: React.FC<AddToCartButtonProps> = (props): React.JSX.Element => {
  const { triggerAddToCart, className } = props;
  const { t } = useTranslation();
  const { negotiation: contextNegotiation } = useNegotiation();
  const { product, isProductBuyable, isSellerOnVacation } = useProductQuery();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const { isRecirculation } = usePdpLayout();
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const isCartProcessing = useContextSelector(CartContext, (v) => v.isCartProcessing);
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const checkoutUrl = useContextSelector(CartContext, (v) => v.checkoutUrl);
  const cartUpdateErrorMessage = useContextSelector(CartContext, (v) => v.cartUpdateErrorMessage);
  const showToastMessage = useContextSelector(ToastMessageContext, (v) => v.showToastMessage);
  const toastMessageId = useContextSelector(ToastMessageContext, (v) => v.toastMessageId);
  const { isSemPDP } = useFeatureFlag();
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isWaitingForAddToCart, setIsWaitingForAddToCart] = useState<boolean>(false);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const CHECKMARK_TIMEOUT = 900;
  const TOASTER_DURATION = 5000;

  const negotiation = useMemo<Negotiation | null>(() => {
    return contextNegotiation?.isClosed ? null : contextNegotiation;
  }, [contextNegotiation]);

  const isAddToCartButtonDisabled = useMemo<boolean>(() => {
    if (negotiation?.isAccepted) {
      return false;
    }

    return !isProductBuyable || isSellerOnVacation || isCartProcessing || isWaitingForAddToCart;
  }, [isCartProcessing, isProductBuyable, isSellerOnVacation, negotiation?.isAccepted, isWaitingForAddToCart]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const handleAddToCart = useCallback(async (): Promise<void> => {
    try {
      await addToShoppingCart(product, true, false);
      setIsSuccess(true);
      successTimerRef.current = setTimeout(() => setIsSuccess(false), CHECKMARK_TIMEOUT);
    } catch {
      if (showToastMessage && toastMessageId < 3) {
        showToastMessage(
          cartUpdateErrorMessage ? cartUpdateErrorMessage : t('PRODUCT.DESCRIPTION.DETAILS.MODAL.TECHNICAL_PROBLEM'),
          '',
          TOASTER_DURATION,
          { type: 'error' },
        );
      }
    } finally {
      setIsWaitingForAddToCart(false);
    }
  }, [addToShoppingCart, cartUpdateErrorMessage, product, showToastMessage, t, toastMessageId]);

  useEffect(() => {
    if (triggerAddToCart) {
      setIsWaitingForAddToCart(true);
      handleAddToCart();
    }
  }, [triggerAddToCart]);

  if (isProductIdInCart(productsInCart, product.id) && !isCartProcessing && !isWaitingForAddToCart) {
    return (
      <Link
        data-cy="pdp_buy_btn"
        data-vc-dd-action-name="pdp_buy_button"
        href={checkoutUrl}
        className={clsx(
          'vc-btn',
          'vc-btn--primary',
          (isRecirculation || isHeroPdpStyles) && !isSemPDP ? 'vc-btn--medium' : 'vc-btn--large',
          'vc-btn--full',
          styles.CTAButtonsBlock__button,
          className,
          (isRecirculation || isHeroPdpStyles) && 'vc-btn--large--md-up',
        )}
      >
        <span className={styles.CTAButtonsBlock__checkoutContent}>
          <span
            className={clsx(styles.CTAButtonsBlock__checkoutCheck, {
              [styles['CTAButtonsBlock__checkoutCheck--visible']]: isSuccess,
            })}
          >
            <span className={styles.CTAButtonsBlock__checkoutCheckLeft}>
              <Icon
                className={styles.CTAButtonsBlock__checkoutCheckIcon}
                name="tick-default"
              />
            </span>
            <span className={styles.CTAButtonsBlock__checkoutCheckRight}>{t('PRODUCT.DETAILS.CTA.ADDED')}</span>
          </span>
          <span
            className={clsx(styles.CTAButtonsBlock__checkoutText, {
              [styles['CTAButtonsBlock__checkoutText--hidden']]: isSuccess,
            })}
          >
            {t('CHAT.HEADER.CTA.VIEW_BAG')}
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Button
      variant="primary"
      size={(isRecirculation || isHeroPdpStyles) && !isSemPDP ? 'medium' : 'large'}
      fullWidth
      withLoader
      loaded={!isCartProcessing && !isWaitingForAddToCart}
      data-cy="pdp_buy_btn"
      data-vc-dd-action-name="pdp_buy_button"
      disabled={isAddToCartButtonDisabled}
      onClick={handleAddToCart}
      className={clsx(
        styles.CTAButtonsBlock__button,
        className,
        (isRecirculation || isHeroPdpStyles) && 'vc-btn--large--md-up',
      )}
    >
      {negotiation && negotiation.isAccepted
        ? t('PRODUCT.DETAILS.CTA.BUY_WITH_PRICE', { negotiation })
        : t('PRODUCT.DETAILS.CTA.BUY')}
    </Button>
  );
};

export default AddToCartButton;
